import React, { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { FiCreditCard, FiLock, FiSettings } from 'react-icons/fi';
import {
  FaRegBookmark,
  FaRegHandshake,
  FaServer,
  FaSignOutAlt,
  FaTachometerAlt,
  FaUser,
  FaUserPlus,
} from 'react-icons/fa';
import { FaChartColumn } from 'react-icons/fa6';
import { IoGameControllerOutline } from 'react-icons/io5';
import { MdKeyboardDoubleArrowRight } from 'react-icons/md';
import { ChevronRight } from 'lucide-react';

import { cn, showToastMessage, useRoleIds } from '@/utils';
import { NavItem, NavItemComponentProps, UserRole } from '@/types';

import { useLogoutMutation } from '@/store/api/authApi';
import { useDispatch, useSelector } from 'react-redux';
import { logout as clearAuth, RootState } from '@/store';
import { clearUser } from '@/store/slices/userSlice';
import apiSlice from '@/store/api/apiSlice';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

const isMobileViewport = () => window.innerWidth < 768;

/** Downline roles each hierarchy level can list and create. */
const DOWNLINE_ROLES: Record<UserRole, UserRole[]> = {
  OWNER: ['SUPER MASTER', 'MASTER', 'AGENT', 'USER'],
  'SUPER MASTER': ['MASTER', 'AGENT', 'USER'],
  MASTER: ['AGENT', 'USER'],
  AGENT: ['USER'],
  USER: [],
  DEMO: [],
  'RESULT MANAGER': [],
};

/** Role groups used in `roles` on nav items. OWNER always sees everything. */
const OWNER_ONLY: UserRole[] = ['OWNER'];
const ALL_HIERARCHY: UserRole[] = ['OWNER', 'SUPER MASTER', 'MASTER', 'AGENT'];

function canAccess(item: NavItem, role: UserRole | undefined) {
  if (!item.roles) return true;
  if (!role) return false;
  return role === 'OWNER' || item.roles.includes(role);
}

/** Filters items (and their children) by role and drops parents left with no children. */
function filterByRole(items: NavItem[], role: UserRole | undefined): NavItem[] {
  return items
    .filter((item) => canAccess(item, role))
    .map((item) =>
      item.children ? { ...item, children: filterByRole(item.children, role) } : item
    )
    .filter((item) => !item.children || item.children.length > 0);
}

function SidebarIcon({
  icon: Icon,
  iconClassName,
  isOpen,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  iconClassName?: string;
  isOpen: boolean;
}) {
  if (!Icon) return null;

  return (
    <span
      className={cn(
        'grid size-[20px] shrink-0 place-items-center rounded-md',
        isOpen && 'mr-3',
        iconClassName
      )}
    >
      <Icon className="h-4 w-4 shrink-0" />
    </span>
  );
}

function NavItemComponent({
  item,
  isOpen,
  level = 0,
  activeMenu,
  setActiveMenu,
  onToggle,
  iconClassName,
}: NavItemComponentProps) {
  const isExpanded = activeMenu === item.name;
  const location = useLocation();

  const hasChildren = item.children && item.children.length > 0;
  const isActive = item.href ? location.pathname === item.href : false;
  const hasActiveChild = item.children?.some(
    (child) => child.href && location.pathname.startsWith(child.href)
  );

  useEffect(() => {
    if (hasActiveChild && isOpen) {
      setActiveMenu(item.name);
    }
  }, [hasActiveChild, isOpen, item.name, setActiveMenu]);

  const handleClick = () => {
    if (hasChildren) {
      setActiveMenu(isExpanded ? null : item.name);
    }
  };
  const Icon = item.icon;
  const indent = level * 16;

  if (hasChildren) {
    return (
      <div>
        <button
          onClick={handleClick}
          // hover:bg-accent hover:text-accent-foreground
          className={cn(
            'flex min-h-10 w-full items-center px-3 py-2 text-base font-medium transition-colors',
            isActive || hasActiveChild ? 'bg-background ' : 'text-gray ',
            !isOpen && 'justify-center px-0'
          )}
          style={{ paddingLeft: isOpen ? `${12 + indent}px` : undefined }}
          title={!isOpen ? item.name : undefined}
        >
          <SidebarIcon icon={Icon} isOpen={isOpen} iconClassName={iconClassName} />

          {isOpen && (
            <>
              <span className="flex-1 text-left text-sm font-normal text-textcolor">
                {item.name}
              </span>

              <ChevronRight
                className={cn(
                  'ml-2 h-4 w-4 transition-transform duration-300 ease-in-out',
                  isExpanded && 'rotate-90'
                )}
              />
            </>
          )}
        </button>

        {/* Tree Menu */}
        {isOpen && (
          <div
            className={cn(
              'ml-6 overflow-hidden transition-all duration-300 ease-in-out',
              isExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
            )}
            aria-hidden={!isExpanded}
          >
            <div className="relative space-y-4 py-2">
              {item.children!.map((child) => (
                <div key={child.name} className="relative">
                  {child.children ? (
                    <NavItemComponent
                      item={child}
                      isOpen={isOpen}
                      level={level + 1}
                      activeMenu={activeMenu}
                      setActiveMenu={setActiveMenu}
                      onToggle={onToggle}
                      iconClassName={child.iconClassName}
                    />
                  ) : (
                    <NavLink
                      onClick={() => {
                        if (isMobileViewport()) {
                          onToggle?.();
                        }
                      }}
                      to={child.href!}
                      className={({ isActive }) =>
                        cn(
                          ' flex gap-2 items-center text-xs text-textcolor sm:text-sm pl-8 transition-colors',
                          isActive ? 'text-secondary font-semibold' : 'text-gray hover:text-blue'
                        )
                      }
                    >
                      <MdKeyboardDoubleArrowRight /> {child.name}
                    </NavLink>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <NavLink
      to={item.href!}
      onClick={() => {
        if (isMobileViewport()) {
          onToggle?.();
        }
      }}
      className={({ isActive }) =>
        cn(
          'flex min-h-10 items-center px-2 py-1.5 sm:py-2 text-sm font-medium transition-colors',
          isActive ? 'bg-background text border-l-2' : 'text-gray ',
          !isOpen && 'justify-center px-0'
        )
      }
      style={{ paddingLeft: isOpen ? `${10 + indent}px` : undefined }}
      title={!isOpen ? item.name : undefined}
    >
      <SidebarIcon icon={Icon} isOpen={isOpen} iconClassName={iconClassName} />

      {isOpen && <span className="text-sm font-normal text-textcolor">{item.name}</span>}
    </NavLink>
  );
}

export function EnhancedSidebar({ isOpen, onToggle }: SidebarProps) {
  const [logout] = useLogoutMutation();
  const dispatch = useDispatch();

  const navigate = useNavigate();
  const userRole = useSelector((state: RootState) => state.user?.data?.role?.name) as
    | UserRole
    | undefined;
  const { superMasterId, masterId, agentId, userId } = useRoleIds();

  const navigation = React.useMemo<NavItem[]>(() => {
    const downline = (userRole && DOWNLINE_ROLES[userRole]) || [];
    const forDownline = (target: UserRole): UserRole[] =>
      ALL_HIERARCHY.filter((role) => DOWNLINE_ROLES[role].includes(target));

    // `roles` decides which hierarchy levels see an item. Omit it to show the item to everyone.
    const items: NavItem[] = [
      {
        name: 'Dashboard',
        href: '/dashboard',
        icon: FaTachometerAlt,
        roles: ALL_HIERARCHY,
      },
      {
        name: 'Market Analysis',
        href: '/sport-analysis',
        icon: FaServer,
        roles: ALL_HIERARCHY,
      },
      {
        name: 'Multi Market',
        href: '/multi-market',
        icon: FaRegBookmark,
        roles: ALL_HIERARCHY,
      },
      {
        name: 'User List',
        icon: FaUser,
        roles: ALL_HIERARCHY,
        children: [
          {
            name: 'Super Master',
            href: `/members/${superMasterId}`,
            roles: forDownline('SUPER MASTER'),
          },
          { name: 'Master', href: `/members/${masterId}`, roles: forDownline('MASTER') },
          { name: 'Agent', href: `/members/${agentId}`, roles: forDownline('AGENT') },
          { name: 'User', href: `/members/${userId}`, roles: forDownline('USER') },
          { name: 'Blocked User', href: '/inactive-members', roles: forDownline('USER') },
        ],
      },
      {
        name: 'User Create',
        icon: FaUserPlus,
        href: '/create-client',
        roles: ALL_HIERARCHY,
        children: [
          {
            name: 'Create Super Master',
            href: `/create-client/${superMasterId}`,
            roles: forDownline('SUPER MASTER'),
          },
          {
            name: 'Create Master',
            href: `/create-client/${masterId}`,
            roles: forDownline('MASTER'),
          },
          { name: 'Create Agent', href: `/create-client/${agentId}`, roles: forDownline('AGENT') },
          { name: 'Create User', href: `/create-client/${userId}`, roles: forDownline('USER') },
        ],
      },
      {
        name: 'Settlement',
        icon: FaRegHandshake,
        href: '/reports/settlement',
        roles: ALL_HIERARCHY,
      },
      {
        name: 'Report',
        icon: FaChartColumn,
        roles: ALL_HIERARCHY,
        children: [
          { name: 'Bet List', href: '/bet-list' },
          { name: 'Account Statement', href: '/rolewise-account-statement' },
          { name: 'Profit Loss', href: '/pl-by-event?event=Sports' },
          { name: 'Chip Statement', href: '/reports/chip-statement' },
          { name: 'Chip Summary', href: '/reports/chip-summary' },
          { name: 'Settlement Report', href: '/pl-by-player' },
          { name: 'Sport Summary Report', href: '/reports/sports-summary' },
          { name: 'Top Clients', href: '/reports/top-clients' },
          { name: 'Settlement', href: '/reports/settlement' },
          // { name: 'Balance Sheet', href: '/reports/balance-sheet' },
        ],
      },
      {
        name: 'Event Management',
        icon: FiCreditCard,
        roles: OWNER_ONLY,
        children: [
          { name: 'Event Fixtures', href: '/event-fixtures' },
          { name: 'Bet Config', href: '/bet-setting' },
        ],
      },
      {
        name: 'Casino Control',
        icon: IoGameControllerOutline,
        href: '/casino-management',
        roles: OWNER_ONLY,
      },
      {
        name: 'Content Management',
        href: '/content-management',
        icon: FiSettings,
        roles: OWNER_ONLY,
      },
      {
        name: 'Change Password',
        icon: FiLock,
        href: '/settings',
      },
    ];

    // Hide user list / create links for roles the current user cannot manage.
    void downline;
    return filterByRole(items, userRole);
  }, [userRole, superMasterId, masterId, agentId, userId]);

  const handleLogout = async () => {
    try {
      await logout().unwrap();
      dispatch(clearAuth());
      dispatch(clearUser());
      apiSlice.util.resetApiState();
      showToastMessage('success', 'You have been logged out successfully.');
      navigate('/login');
    } catch (err: any) {
      showToastMessage('error', err?.data?.message || 'Failed to logout. Please try again.');
    }
  };
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  return (
    <>
      {/* Sidebar backdrop for mobile */}
      {isOpen && (
        <div className="fixed inset-0 z-20 bg-sidebar/20   lg:hidden" onClick={onToggle} />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-30 flex flex-col text-gray bg-sidebar transition-all duration-300 overflow-hidden  border-r border-r-green3',
          isOpen ? 'w-[243px] translate-x-0' : 'w-16 -translate-x-full lg:translate-x-0'
        )}
      >
        {/* Sidebar header */}
        <div className={cn('flex h-[65px] items-center ', isOpen ? 'px-4' : 'justify-center px-2')}>
          <div className={cn('flex w-full justify-center', !isOpen && '')}>
            <img
              alt="Logo"
              className={cn('object-contain', isOpen ? 'h-12 max-w-[142px]' : 'h-8 w-10')}
              src="/images/logo.png"
            />
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1 overflow-y-auto scrollbar-hide ">
          {navigation.map((item) => (
            <NavItemComponent
              key={item.name}
              iconClassName={item.iconClassName}
              item={item}
              isOpen={isOpen}
              activeMenu={activeMenu}
              setActiveMenu={setActiveMenu}
              onToggle={onToggle}
            />
          ))}
        </nav>

        {/* Logout */}

        <div
          onClick={handleLogout}
          className={cn(
            'flex items-center cursor-pointer text-xs sm:text-sm font-medium text-gray border-t border-t-green3 py-2 sm:py-5 px-2',
            isOpen ? 'gap-3' : 'justify-center'
          )}
          title={!isOpen ? 'Logout' : undefined}
        >
          <FaSignOutAlt className="h-5 w-5 shrink-0" />
          {isOpen && 'Logout'}
        </div>
      </div>
    </>
  );
}
